import React, { useState } from 'react';
import { supabase } from '../../services/supabaseClient';
import { describeError } from '../../services/errorMessage';
import { SignaturePad } from '../common/SignaturePad';

/**
 * Oeffentliche Unterschriften-Seite fuers Smartphone (#/sign/<token>).
 * Wird OHNE Login gerendert (index.tsx routet am Auth-Gate vorbei); der
 * Rueckweg laeuft ausschliesslich ueber die anon-RPC submit_signature —
 * kein Tabellenzugriff mit dem anon-Key (siehe crm_quotes_v5.sql).
 *
 * Das Zeichnen selbst steckt seit der Einweisung in den SiGe-Plan in
 * components/common/SignaturePad (zweiter Nutzer derselben Canvas-Behandlung).
 * Hier bleibt nur, was diese Seite ausmacht: der anon-RPC-Rueckweg und die
 * Erfolgsmeldung. `maxBreite` = Canvas-Breite, damit die uebertragene DataURL
 * unveraendert die volle Aufloesung traegt wie bisher.
 *
 * Zweiter Nutzer seit dem SiFa-Modul: die QR-Bruecke der Unterweisung
 * (#/sign/<token>?u=1). Dort ist die Zieltabelle eine andere
 * (sifa_signatur_anfragen), also eine eigene RPC `sifa_submit_signature` mit
 * `p_qr_token` — der Parameter `rpc` schaltet um, alles andere bleibt gleich.
 */
export type SignaturePadRpc = 'submit_signature' | 'sifa_submit_signature';

export const SignaturePadPage: React.FC<{ token: string; quoteNumber?: string; rpc?: SignaturePadRpc }> = ({
  token,
  quoteNumber,
  rpc = 'submit_signature',
}) => {
  const [state, setState] = useState<'draw' | 'sending' | 'done' | 'error'>('draw');
  const [errorMsg, setErrorMsg] = useState('');
  const sifa = rpc === 'sifa_submit_signature';

  const submit = async (dataUrl: string) => {
    setState('sending');
    try {
      const params = sifa ? { p_qr_token: token, p_signature: dataUrl } : { p_token: token, p_signature: dataUrl };
      const { data, error } = await supabase.rpc(rpc, params);
      if (error) throw error;
      if (!data) throw new Error('Link abgelaufen oder bereits verwendet. Bitte am PC einen neuen QR-Code erzeugen.');
      setState('done');
    } catch (e) {
      setErrorMsg(describeError(e));
      setState('error');
    }
  };

  const titel = sifa
    ? 'Unterschrift — Unterweisung'
    : `Unterschrift${quoteNumber ? ` — Angebot ${quoteNumber}` : ''}`;
  const hinweis = sifa
    ? 'Bitte unten im Feld unterschreiben und absenden. Die Unterschrift wird auf deinem Nachweis abgedruckt.'
    : 'Bitte unten im Feld unterschreiben und absenden. Die Unterschrift wird in deinem Profil hinterlegt.';

  if (state === 'done') {
    return (
      <div
        className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4 gap-4"
        style={{ minHeight: '100dvh', paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 w-full max-w-2xl space-y-4">
          <h1 className="text-base font-bold text-slate-800">{titel}</h1>
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
            Unterschrift übertragen — du kannst dieses Fenster schließen.
          </div>
        </div>
      </div>
    );
  }

  return (
    <SignaturePad
      titel={titel}
      hinweis={hinweis}
      // SiFa: 480 px wie der direkte Weg auf der Teilnehmerseite — die
      // DataURL landet in sifa_teilnahmen.unterschrift und im Zertifikat-PDF.
      maxBreite={sifa ? 480 : 900}
      uebernehmenLabel="Absenden"
      busy={state === 'sending'}
      fehler={state === 'error' ? errorMsg : null}
      onUebernehmen={submit}
    />
  );
};
